// src/features/admin/services/SystemPreferencesAPI.ts

import type { AppPreferences } from '@/types/appPreferences';

const API_BASE_URL = '/api/v1/admin/system-preferences';

/**
 * Get authentication token from Keycloak or storage
 */
function getAuthToken(): string | null {
  // Try Keycloak instance first (primary method)
  const keycloak = (window as any).keycloak;
  if (keycloak?.token) {
    return keycloak.token;
  }

  // Fallback to localStorage/sessionStorage
  return (
    localStorage.getItem('webelieve_token') ||
    sessionStorage.getItem('webelieve_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    localStorage.getItem('webelieve_access_token')
  );
}

/**
 * System Preferences Interface
 */
export interface OperatingContext {
  type: 'region' | 'regulation' | 'industry' | 'business_function' | 'business_unit' | 'market' | 'legal_jurisdiction' | 'custom' | string;
  code: string;
  label: string;
  enabled: boolean;
  extension?: string;
}

export interface ServiceArea {
  code: string;
  label: string;
  category?: string;
  enabled: boolean;
  extension?: string;
}

export interface CapabilityPackBinding {
  id?: string;
  service_area_code: string;
  operating_context_codes: string[];
  capability_pack_code: string;
  enabled: boolean;
  extension?: string;
}

export interface SystemPreferences {
  id?: string;
  subscriber_id?: string;
  realm_id?: string;
  
  // Regional & Localization
  timezone: string;
  date_format: string;
  time_format: string;
  currency: string;
  language: string;
  business_regions?: string[];
  operating_contexts: OperatingContext[];
  service_areas: ServiceArea[];
  capability_pack_bindings: CapabilityPackBinding[];
  legal_jurisdictions?: string[];
  
  // Typography & Display
  system_font: string;
  document_font: string;
  font_size_scale: string;
  contrast_mode: string;
  measurement_unit: string;

  // UI Appearance Defaults
  ui_theme_mode?: string;
  ui_accent_color?: string;
  ui_accent_custom_hex?: string;
  ui_density?: string;
  ui_neutral_base?: string;
  ui_app_defaults?: AppPreferences;
  
  // Document Defaults
  default_page_size: string;
  default_page_orientation: string;
  default_margin_preset: string;
  default_line_spacing: string;
  default_line_numbering: boolean;
  default_show_page_numbers: boolean;
  
  // Security & Compliance
  session_timeout_minutes: number;
  require_2fa: boolean;
  audit_logging_enabled: boolean;
  confidentiality_mode_enabled: boolean;
  document_watermarking_enabled: boolean;
  
  // Calendar & Scheduling
  business_hours_start: string;
  business_hours_end: string;
  default_meeting_duration_minutes: number;
  default_reminder_minutes: number;
  weekend_saturday: boolean;
  weekend_sunday: boolean;
  holiday_calendars: string[];
  
  // Metadata
  created_at?: string;
  updated_at?: string;
}

/**
 * Get system preferences for the authenticated WeBelieve subscriber realm.
 */
export const getSystemPreferences = async (): Promise<SystemPreferences> => {
  const token = getAuthToken();
  
  const response = await fetch(API_BASE_URL, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch system preferences: ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
};

/**
 * Update system preferences
 */
export const updateSystemPreferences = async (
  preferences: Partial<SystemPreferences>
): Promise<SystemPreferences> => {
  const token = getAuthToken();
  
  const response = await fetch(API_BASE_URL, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(preferences),
  });

  if (!response.ok) {
    throw new Error(`Failed to update system preferences: ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
};

/**
 * Reset system preferences to defaults
 */
export const resetSystemPreferences = async (): Promise<SystemPreferences> => {
  const token = getAuthToken();
  
  const response = await fetch(`${API_BASE_URL}/reset`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to reset system preferences: ${response.statusText}`);
  }

  const result = await response.json();
  return result.data;
};
